import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import AuthLayout from "../../../components/layout/AuthLayout";
import { db } from "../../../firebase/firebase";

const CLASS_OPTIONS = ["1AD", "2AD", "3AD", "4AD", "5AD", "Andere"];
const MAX_CLASSES = 3;

const ROLE_OPTIONS = [
  {
    value: "teacher",
    title: "Lehrkraft",
    description: "Für Lehrpersonen mit Klassenbezug und Schülerübersicht.",
  },
  {
    value: "viewer",
    title: "Einsicht / Sonstiges",
    description: "Für eingeschränkten Zugriff oder andere schulische Rollen.",
  },
];

function formatRequestedClassLabel(classes) {
  if (!Array.isArray(classes) || classes.length === 0) {
    return "";
  }

  if (classes.length === 1) {
    return classes[0];
  }

  return classes.join(", ");
}

export default function RequestAccessPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "teacher",
    schoolName: "",
    selectedClasses: [],
    customClass: "",
    message: "",
  });

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const hasOtherClass = form.selectedClasses.includes("Andere");

  const normalizedClasses = useMemo(() => {
    const selectedClasses = form.selectedClasses
      .filter((entry) => entry !== "Andere")
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);

    if (hasOtherClass && form.customClass.trim()) {
      selectedClasses.push(form.customClass.trim());
    }

    return selectedClasses.slice(0, MAX_CLASSES);
  }, [form.selectedClasses, form.customClass, hasOtherClass]);

  const isFormValid = useMemo(() => {
    if (!form.name.trim()) return false;
    if (!form.email.trim()) return false;
    if (!form.schoolName.trim()) return false;
    if (!form.selectedClasses.length) return false;
    if (hasOtherClass && !form.customClass.trim()) return false;
    return normalizedClasses.length > 0;
  }, [form, hasOtherClass, normalizedClasses]);

  function handleChange(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function toggleClass(className) {
    setForm((prev) => {
      const isSelected = prev.selectedClasses.includes(className);

      if (isSelected) {
        const nextClasses = prev.selectedClasses.filter((entry) => entry !== className);

        return {
          ...prev,
          selectedClasses: nextClasses,
          customClass: className === "Andere" ? "" : prev.customClass,
        };
      }

      if (prev.selectedClasses.length >= MAX_CLASSES) {
        setError(`Du kannst maximal ${MAX_CLASSES} Klassen anfragen.`);
        return prev;
      }

      setError("");

      return {
        ...prev,
        selectedClasses: [...prev.selectedClasses, className],
      };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setInfo("");

    if (!form.name.trim() || !form.email.trim() || !form.schoolName.trim()) {
      setError("Bitte Name, E-Mail und Schule ausfüllen.");
      return;
    }

    if (!form.selectedClasses.length) {
      setError("Bitte mindestens eine Klasse auswählen.");
      return;
    }

    if (hasOtherClass && !form.customClass.trim()) {
      setError("Bitte die andere Klasse angeben.");
      return;
    }

    if (!normalizedClasses.length) {
      setError("Bitte mindestens eine gültige Klasse angeben.");
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, "accessRequests"), {
        name: form.name.trim(),
        email: form.email.trim(),
        authUid: null,
        role: form.role,
        schoolName: form.schoolName.trim(),
        requestedClasses: normalizedClasses,
        requestedClassLabel: formatRequestedClassLabel(normalizedClasses),
        message: form.message.trim(),
        status: "pending",
        source: "admin-panel",
        createdAt: serverTimestamp(),
        onboardingState: "pending-review",
        passwordSetupStatus: "not-sent",
      });

      setInfo(
        "Anfrage erfolgreich gesendet. Dein Zugang wird jetzt geprüft. Nach der Freigabe bekommst du automatisch eine E-Mail, über die du dein Passwort festlegen kannst. Erst danach ist die Anmeldung möglich."
      );

      setForm({
        name: "",
        email: "",
        role: "teacher",
        schoolName: "",
        selectedClasses: [],
        customClass: "",
        message: "",
      });
    } catch (submitError) {
      console.error("ACCESS REQUEST ERROR:", submitError);
      setError(
        submitError?.message || "Die Anfrage konnte gerade nicht gespeichert werden."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout title="Zugang anfordern | AceTime Admin-Panel">
      <div className="ac-authcard-inner">
        <div className="ac-login-head">
          <div className="ac-login-badge">Zugang anfordern</div>
          <h1 className="ac-login-title">Admin-Zugang beantragen</h1>
          <p className="ac-login-subtitle">
            Fülle die Anfrage kurz aus. Danach wird geprüft, welche Klassen und
            welche Zugriffsart für dich passen.
          </p>
        </div>

        {error && (
          <div className="ac-login-error">
            <span className="ac-login-erroricon">!</span>
            <span>{error}</span>
          </div>
        )}

        {info && (
          <div className="ac-login-success">
            <span className="ac-login-erroricon">✓</span>
            <span>{info}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="ac-login-form">
          <div className="ac-field">
            <label className="ac-label">Name</label>
            <input
              className="ac-input ac-input-plain"
              type="text"
              value={form.name}
              onChange={(event) => handleChange("name", event.target.value)}
              placeholder="z. B. Max Mustermann"
              autoComplete="name"
            />
            <div className="ac-field-help">Bitte Vor- und Nachnamen angeben.</div>
          </div>

          <div className="ac-field">
            <label className="ac-label">E-Mail</label>
            <input
              className="ac-input ac-input-plain"
              type="email"
              value={form.email}
              onChange={(event) => handleChange("email", event.target.value)}
              placeholder="z. B. max@schule.at"
              autoComplete="email"
            />
            <div className="ac-field-help">
              Am besten die schulische E-Mail-Adresse verwenden.
            </div>
          </div>

          <div className="ac-field">
            <label className="ac-label">Zugriffsart</label>
            <div className="ac-role-grid">
              {ROLE_OPTIONS.map((roleOption) => {
                const isActive = form.role === roleOption.value;

                return (
                  <button
                    key={roleOption.value}
                    type="button"
                    className={`ac-role-card ${isActive ? "is-active" : ""}`}
                    onClick={() => handleChange("role", roleOption.value)}
                  >
                    <span className="ac-role-card__title">{roleOption.title}</span>
                    <span className="ac-role-card__text">
                      {roleOption.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ac-field">
            <label className="ac-label">Schule</label>
            <input
              className="ac-input ac-input-plain"
              type="text"
              value={form.schoolName}
              onChange={(event) => handleChange("schoolName", event.target.value)}
              placeholder="z. B. BHAK Wien 10"
            />
          </div>

          <div className="ac-field">
            <label className="ac-label">Klassen auswählen</label>

            <div className="ac-class-grid">
              {CLASS_OPTIONS.map((className) => {
                const isActive = form.selectedClasses.includes(className);

                return (
                  <button
                    key={className}
                    type="button"
                    className={`ac-class-chip ${isActive ? "is-active" : ""}`}
                    onClick={() => toggleClass(className)}
                  >
                    {className}
                  </button>
                );
              })}
            </div>

            <div className="ac-field-help">
              Du kannst bis zu {MAX_CLASSES} Klassen anfragen. Lehrkräfte sehen später
              nur die freigegebenen Klassen.
            </div>

            {!!normalizedClasses.length && (
              <div className="ac-field-help" style={{ marginTop: 8 }}>
                Ausgewählt: {formatRequestedClassLabel(normalizedClasses)}
              </div>
            )}
          </div>

          {hasOtherClass && (
            <div className="ac-field">
              <label className="ac-label">Andere Klasse angeben</label>
              <input
                className="ac-input ac-input-plain"
                type="text"
                value={form.customClass}
                onChange={(event) => handleChange("customClass", event.target.value)}
                placeholder="z. B. 2BK oder 4CK"
              />
            </div>
          )}

          <div className="ac-field">
            <label className="ac-label">Nachricht</label>
            <textarea
              className="ac-request-textarea"
              value={form.message}
              onChange={(event) => handleChange("message", event.target.value)}
              placeholder="Optional: kurze Info, warum du Zugriff brauchst."
              rows={5}
            />
          </div>

          <button
            type="submit"
            className="ac-login-submit"
            disabled={loading || !isFormValid}
          >
            {loading ? "Wird gesendet..." : "Anfrage absenden"}
          </button>
        </form>

        <button
          type="button"
          className="ac-login-linkbtn"
          onClick={() => navigate("/login")}
        >
          Zurück zum Login
        </button>

        <div className="ac-login-meta">
          <span className="ac-login-meta-label">AceTime • Admin-Panel</span>
        </div>
      </div>
    </AuthLayout>
  );
}